import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bffClient } from '@sluitstraat/core';
import { buildRechtsbijstandPayload, mergeRechtsbijstandResult } from './calc';

// Owns the coverage premium auto-calculation: builds the payload, calls the BFF, merges the
// result, and re-fires when the priced inputs change (debounced) or on mount when the section
// premiums are missing. Extracted from RechtsbijstandScreen so the screen keeps only state +
// render + navigation.

// Hash of the priced inputs — shared by the debounced auto-calc effect, the mount claim and
// the preview-price cache so all three agree on "did anything price-relevant change".
export const computeRbvCalcInputHash = (d: any): string => {
  const ba = d?.Contractdocument?.PP?.BA;
  const vk = d?.Contractdocument?.PP?.VK;
  const rpE = d?.Contractdocument?.PP?._rpEigendom;
  return JSON.stringify({
    street: ba?.BA_STRAAT,
    gezsamst: vk?.VK_GEZSAMST,
    rpVwe: rpE?.RP_VWE,
    rpVbe: rpE?.RP_VBE,
    rpVgavrh: rpE?.RP_VGAVRH,
  });
};

// sessionStorage cache of the all-options pricing calc, keyed by the input hash it was priced
// with. STRICTLY display-only: the coverage cards fall back to it for the "+ EUR x" preview of
// unselected coverages after handleNext stripped them from the step data. It is never merged
// into the step data, so the calculation/package endpoints only ever see the selected prices.
const COVERAGE_PRICES_CACHE_KEY = 'rechtsbijstand_coverage_prices';

export interface RbvPreviewPrices {
  sections: Record<string, any>;
  rp: any[];
}

export const readCoveragePricesCache = (): ({ hash: string } & RbvPreviewPrices) | null => {
  try {
    const stored = sessionStorage.getItem(COVERAGE_PRICES_CACHE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const writeCoveragePricesCache = (hash: string, pp: any) => {
  try {
    const sections: Record<string, any> = {};
    for (const key of ['WC', 'VK', 'IN', 'FV']) {
      if (pp?.[key]) sections[key] = pp[key];
    }
    const rp = Array.isArray(pp?.RP) ? pp.RP : [];
    sessionStorage.setItem(COVERAGE_PRICES_CACHE_KEY, JSON.stringify({ hash, sections, rp }));
  } catch {
    // Quota exceeded: the cache is a display optimization only.
  }
};

// All inputs the BFF needs to price the coverages are present.
const allRequiredFieldsFilled = (d: any): boolean => {
  const ba = d?.Contractdocument?.PP?.BA;
  const vk = d?.Contractdocument?.PP?.VK;
  if (!ba?.BA_STRAAT || !ba?.BA_PLAATS) return false;
  if (!vk?.VK_GEZSAMST) return false;
  // Rental units must total 1–2 when Eigendom & Verhuur is enabled.
  if (d?.Supportsection?.Dekkingen?.Eigendom_Verhuur_Onroerende_Zaken === 'J') {
    const rpE = d?.Contractdocument?.PP?._rpEigendom || {};
    const totalUnits = (rpE.RP_VWE ?? 1) + (rpE.RP_VBE ?? 0) + (rpE.RP_VGAVRH ?? 0);
    if (totalUnits < 1 || totalUnits > 2) return false;
  }
  return true;
};

export function useCoverageCalculation(
  data: any,
  persist: (updater: (prev: any) => any) => void,
) {
  const { t } = useTranslation();
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [lastCalculationInput, setLastCalculationInput] = useState<any>(null);
  const [lastCalculationOutput, setLastCalculationOutput] = useState<any>(null);
  const lastCalcHashRef = useRef<string>('');

  // Run a calculation with all dekkingen to price every coverage.
  const handleCalculateAll = useCallback(async () => {
    try {
      setIsCalculating(true);
      setCalculationError(null);

      const payload = buildRechtsbijstandPayload(data);
      setLastCalculationInput(payload);

      const bffResult = await bffClient.request<any>('POST', '/api/v1/productservice/calculate', payload);
      setLastCalculationOutput(bffResult);

      if (bffResult?.output) {
        // Cache the all-options prices for the coverage-card previews, keyed by the inputs
        // this calc was priced with. Display-only — the step data merge below stays the
        // single source for everything that reaches the calculation/package endpoints.
        writeCoveragePricesCache(computeRbvCalcInputHash(data), bffResult.output?.Contractdocument?.PP);
        persist((prevData: any) => mergeRechtsbijstandResult(bffResult.output, prevData));
      }
    } catch (error: any) {
      console.error('[Rechtsbijstand] Calculation error:', error);
      setCalculationError(error?.message || t('common.premiumCalculationError'));
      setLastCalculationOutput({ error: error?.message || String(error) });
    } finally {
      setIsCalculating(false);
    }
  }, [data, persist, t]);

  // Hash of the priced inputs — when it changes, re-calculate (debounced).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the hash inputs are enumerated on purpose; data as a whole would recompute per keystroke
  const calcInputHash = useMemo(() => computeRbvCalcInputHash(data), [
    data?.Contractdocument?.PP?.BA?.BA_STRAAT,
    data?.Contractdocument?.PP?.VK?.VK_GEZSAMST,
    data?.Contractdocument?.PP?._rpEigendom?.RP_VWE,
    data?.Contractdocument?.PP?._rpEigendom?.RP_VBE,
    data?.Contractdocument?.PP?._rpEigendom?.RP_VGAVRH,
  ]);

  // Preview prices for unselected coverages: valid only while the cached pricing matches the
  // current inputs; any change misses here and the debounced recalc refreshes the cache.
  const previewPrices: RbvPreviewPrices | null = useMemo(() => {
    const cache = readCoveragePricesCache();
    return cache && cache.hash === calcInputHash ? { sections: cache.sections, rp: cache.rp } : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isCalculating is the deliberate re-read trigger after a calculation rewrote the cache
  }, [calcInputHash, isCalculating]);

  useEffect(() => {
    if (isCalculating) return;
    if (!allRequiredFieldsFilled(data)) return;
    if (calcInputHash === lastCalcHashRef.current) return;
    const timer = setTimeout(() => {
      lastCalcHashRef.current = calcInputHash;
      handleCalculateAll();
    }, 300);
    return () => clearTimeout(timer);
  }, [calcInputHash, isCalculating, handleCalculateAll, data]);

  // On mount / re-entry (e.g. navigating back from summary): if the inputs are filled but the
  // section BTPs aren't populated, force an immediate recalculation. Downstream chain endpoints
  // strip section premium data but leave the identifying inputs intact; the debounced effect
  // above can miss that on remount, so this guarantees the fresh calc.
  useEffect(() => {
    if (isCalculating) return;
    if (!allRequiredFieldsFilled(data)) return;
    const pp = data?.Contractdocument?.PP;
    const hasSectionPremiums =
      Number(pp?.WC?.WC_BTP) > 0 ||
      Number(pp?.VK?.VK_BTP) > 0 ||
      Number(pp?.IN?.IN_BTP) > 0 ||
      Number(pp?.FV?.FV_BTP) > 0 ||
      (Array.isArray(pp?.RP) && pp.RP.some((rp: any) => Number(rp?.RP_BTP) > 0));
    if (!hasSectionPremiums) {
      lastCalcHashRef.current = '';
      handleCalculateAll();
    } else {
      lastCalcHashRef.current = calcInputHash;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isCalculating,
    calculationError,
    setCalculationError,
    lastCalculationInput,
    lastCalculationOutput,
    handleCalculateAll,
    previewPrices,
  };
}
